/**
 * Audio engine. Plays Ludo.ai-generated samples (public/audio/*.mp3) when available — cannon fire,
 * impacts, splashes, coins, a looping ocean ambiance and two music themes (calm "sail" bed that
 * crossfades to an "battle" theme during combat) — and FALLS BACK to 100%-synthesized Web Audio
 * (oscillators + filtered noise) for any sample that fails to load, so the game is never silent.
 *
 * One-shots are spatialised: attenuated by distance from the local ship and panned by broadside.
 * Browsers block AudioContext until a user gesture, so the context is built lazily on the first
 * pointer/key (wired in main.ts). Everything no-ops safely if Web Audio is unavailable or muted.
 */

const MAX_DIST = 220;        // beyond this a one-shot is inaudible (matches the AOI feel)
const VOICE_CAP = 14;        // concurrent one-shots; excess is dropped (cheap, avoids clipping storms)
const AUDIO_BASE = '/audio/';
/** Generated samples + per-sound playback gain (samples are normalised loud, so trim to taste). */
const SAMPLES: Record<string, number> = {
  cannon_fire: 0.95, cannon_hit: 0.95, splash: 0.7, ram: 0.95, sink: 0.8, coins: 0.7, grapple: 0.6, anchor: 0.8,
  thunder: 0.9, thunder2: 0.9, thunder3: 0.85, storm_rain: 1,
  // wave 2b activity SFX
  fishing_cast: 0.8, fishing_bite: 0.9, fishing_catch: 0.85, dive_splash: 0.9, surface_gasp: 0.85,
  brew_potion: 0.8, forge_hammer: 0.85, market_trade: 0.8, page_turn: 0.7, gulls_loop: 1,
  // wave 3 event SFX (files present, now registered)
  whale_call: 0.95, bounty_wanted: 0.9, cursed_sting: 0.9,
  ambient_sea: 1, music_battle: 1,
  music_sail: 1, music_calm1: 1, music_calm2: 1, music_calm3: 1, music_night: 1, music_night2: 1, music_voyage: 1, music_menu: 1,
  // wave 6: the walkable port — a plaza ambiance bed + a tavern ambiance/music that crossfades in near the inn
  port_ambiance: 1, tavern_ambiance: 1, music_tavern: 1,
  // Art+Audio wave: tavern-casino one-shots + the Capital city crowd bed
  card_deal: 0.7, chip_bet: 0.75, slot_spin: 0.8, slot_win: 0.9, city_ambiance: 1,
};
/** Daytime relaxing tracks rotated as the background bed. */
const CALM_TRACKS = ['music_sail', 'music_calm1', 'music_calm2', 'music_calm3', 'music_voyage', 'music_menu'];
/** Nocturnal tracks; one loops as the night bed and crossfades in when the day/night cycle goes dark. */
const NIGHT_TRACKS = ['music_night2', 'music_night'];
/** Thunder variations — a random loaded one plays per lightning strike so it never repeats the same clip. */
const THUNDERS = ['thunder', 'thunder2', 'thunder3'];
/**
 * COLD-LOAD DIET (mobile retention). The ten ~1.3 MB music tracks total ~12.6 MB — the single biggest chunk a
 * newcomer used to pull the instant they tapped, because loadSamples() fetched the WHOLE SAMPLES bank on the
 * first gesture. They are now EXCLUDED from that eager fetch and streamed lazily, one per context, via
 * ensureMusic(): the entry bed is a single calm track (ENTRY_MUSIC); the battle / night / tavern themes arrive
 * only as their context nears; the rest of the calm rotation trickles in slowly in the background. Zero wire.
 */
const MUSIC_TRACKS = new Set<string>([...CALM_TRACKS, ...NIGHT_TRACKS, 'music_battle', 'music_tavern']);
/** The one track a fresh session streams on the first gesture — also a calm-bed member, so it seeds the daytime
 *  rotation with no extra download. */
const ENTRY_MUSIC = 'music_menu';
/** How slowly the remaining calm tracks trickle in behind the entry track (ms between fetches) — keeps the
 *  background variety fill off the entry burst and gentle on mobile data. */
const CALM_DRIP_MS = 26000;

export class Audio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;   // 1s of white noise, reused by every synthesized sound
  private slotWhir: { src: AudioBufferSourceNode; gain: GainNode } | null = null; // the sustained reel-rush loop
  private voices = 0;
  private muted = false;
  // Per-category volume buses (Settings › Audio). Every source routes through one of these before master:
  // one-shots → sfxCat, ambiance beds → ambCat, the music bus → musCat. Gains default to 1.0 (== shipped).
  private sfxCat: GainNode | null = null;
  private ambCat: GainNode | null = null;
  private musCat: GainNode | null = null;
  /** Onda Social (v40): a dedicated bus for incoming WebRTC voice (its own Settings › Audio slider). Peer
   *  panners connect here so voice never touches the existing sfx/ambiance/music buses. */
  private voiceCat: GainNode | null = null;
  private masterVol = 1;   // 0..1 slider gain, multiplies the internal 0.6 headroom
  private sfxVol = 1;
  private ambVol = 1;
  private musVol = 1;
  private voiceVol = 1;
  private ambientGain: GainNode | null = null;
  // sample bank + bed/music state
  private samples = new Map<string, AudioBuffer>();
  private armed = false;            // startAmbient() was called (a gesture happened)
  private ambientStarted = false;
  private musicStarted = false;
  private musicBus: GainNode | null = null;
  private calmGain: GainNode | null = null;     // the rotating relaxing day bed channel
  private nightGain: GainNode | null = null;    // the nocturnal bed (crossfaded in at night)
  private battleGain: GainNode | null = null;   // the combat theme (silent until combatPing)
  private calmSrc: AudioBufferSourceNode | null = null;
  private calmIdx = 0;                           // position in the rotating relaxing playlist
  private inCombat = false;                       // battle music owns the mix while true
  private nightMix = 0;                           // 0 day-bed … 1 night-bed (follows the cycle)
  private combatTimer: ReturnType<typeof setTimeout> | null = null;
  private stormGain: GainNode | null = null;      // looping wind+rain bed, volume tracks storm intensity
  private stormBedLevel = -1;                      // last set storm-bed gain (throttle per-frame writes)
  /** The PROCEDURAL storm rig — every layer synthesized, zero bytes downloaded (see `ensureStormBed`). */
  private stormRig: {
    wind: GainNode; windFilter: BiquadFilterNode;   // the gale itself: filtered noise, opens up with fury
    gust: GainNode; gustLfo: OscillatorNode;         // a slow breath on the wind so it never sits flat
    rain: GainNode;                                  // hiss on the deck
    wash: GainNode; washFilter: BiquadFilterNode;    // the sea's own roar (breaking water, not rain)
  } | null = null;
  private stormLevel = 0;                            // last intensity handed to setStorm (drives creaks)
  private creakAt = 0;                               // ctx time the next hull creak is due
  // wave 6: walkable-port beds (plaza ambiance + tavern ambiance/music), built lazily on first entry
  private portBed: { port: GainNode; tavAmb: GainNode; tavMusic: GainNode } | null = null;
  private cityGain: GainNode | null = null;   // the Capital-only crowd murmur bed (a discreet layer over the port ambiance)
  private ashore = false;

  /** Lazily build the context (call from a user gesture; safe to call repeatedly). */
  resume(): void {
    try {
      if (!this.ctx) {
        const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) return;
        this.ctx = new Ctor();
        this.master = this.ctx.createGain();
        this.master.gain.value = this.muted ? 0 : 0.6 * this.masterVol;
        // master-bus limiter: stops the mix from clipping/harsh-saturating when a
        // dozen booms stack in a big fight (mobile speakers distort badly otherwise).
        const comp = this.ctx.createDynamicsCompressor();
        comp.threshold.value = -8; comp.knee.value = 24; comp.ratio.value = 4;
        comp.attack.value = 0.003; comp.release.value = 0.25;
        this.master.connect(comp); comp.connect(this.ctx.destination);
        // category buses sit between every source and master (all default 1.0 → identical to the old direct mix)
        this.sfxCat = this.ctx.createGain(); this.sfxCat.gain.value = this.sfxVol; this.sfxCat.connect(this.master);
        this.ambCat = this.ctx.createGain(); this.ambCat.gain.value = this.ambVol; this.ambCat.connect(this.master);
        this.musCat = this.ctx.createGain(); this.musCat.gain.value = this.musVol; this.musCat.connect(this.master);
        this.voiceCat = this.ctx.createGain(); this.voiceCat.gain.value = this.voiceVol; this.voiceCat.connect(this.master);
        this.noise = this.makeNoise(1);
        void this.loadSamples();
        // safety: if the generated ambiance never loads, still give the player the synth ocean bed
        setTimeout(() => { if (this.armed && !this.ambientStarted) this.startSynthAmbient(); }, 3000);
      }
      if (this.ctx.state === 'suspended') void this.ctx.resume();
    } catch { /* no audio — stay silent */ }
  }

  /** Fetch + decode every generated sample in parallel; missing/failed ones just fall back to synth.
   *  Each resolves independently and pokes ensureAudioBeds(), so the bed/music start as soon as the
   *  first usable tracks arrive (we never wait for the whole ~11MB before any audio plays); late
   *  calm tracks simply join the rotation. */
  private async loadSamples(): Promise<void> {
    if (!this.ctx) return;
    // COLD-LOAD DIET: skip the ten music tracks (~12.6 MB) here — they stream lazily per context via ensureMusic().
    // Only the small SFX one-shots + ambience beds ride the first gesture.
    await Promise.all(Object.keys(SAMPLES).filter((n) => !MUSIC_TRACKS.has(n)).map(async (name) => {
      try {
        const r = await fetch(`${AUDIO_BASE}${name}.mp3`);
        if (!r.ok) return;
        const buf = await this.ctx!.decodeAudioData(await r.arrayBuffer());
        this.samples.set(name, buf);
        this.ensureAudioBeds();
      } catch { /* fall back to synth for this one */ }
    }));
    this.ensureAudioBeds();
  }

  /* -------------------------------- lazy music streaming (cold-load diet) -------------------------------- */
  private musicRequested = new Set<string>();  // tracks whose fetch has been kicked (never re-fetched)
  private battleSrc: AudioBufferSourceNode | null = null;
  private nightSrc: AudioBufferSourceNode | null = null;
  private tavMusicSrc: AudioBufferSourceNode | null = null;  // the port bed's tavern-music loop (may attach late)
  private calmDripTimer: ReturnType<typeof setTimeout> | null = null;

  /** Stream a single music track on demand, decode it into the sample bank, then wire it into whatever bed it
   *  belongs to (calm rotation / battle / night / tavern). Idempotent per track; no-op before a gesture built
   *  the ctx, and silently leaves that theme to synth/absent on a failed fetch. */
  ensureMusic(name: string): void {
    if (!this.ctx || this.samples.has(name) || this.musicRequested.has(name)) return;
    this.musicRequested.add(name);
    void (async () => {
      try {
        const r = await fetch(`${AUDIO_BASE}${name}.mp3`);
        if (!r.ok) return;
        this.samples.set(name, await this.ctx!.decodeAudioData(await r.arrayBuffer()));
        this.onMusicLoaded(name);
      } catch { /* leave this theme silent — the bed keeps sequencing the tracks that did land */ }
    })();
  }

  /** A lazily-streamed music track just landed — start the music bed if it hasn't started, and attach the track
   *  to its channel (battle/night as a silent-until-cued loop, calm tracks join the rotation immediately). */
  private onMusicLoaded(name: string): void {
    this.ensureAudioBeds(); // starts the music bus the first time any music arrives (if armed)
    if (name === 'music_battle') this.attachBattleLoop();
    else if (NIGHT_TRACKS.includes(name)) this.attachNightLoop();
    else if (CALM_TRACKS.includes(name)) { if (this.calmGain && !this.calmSrc) this.playNextCalm(); } // seed rotation if idle
    else if (name === 'music_tavern') this.attachTavernMusicLoop(); // wire it in if the port bed already built without it
  }

  /** Attach the tavern-music loop into an already-built port bed (streamed lazily, so it can land after the bed
   *  was assembled on the player's first ashore frame). No-op if the bed hasn't been built or it's already on. */
  private attachTavernMusicLoop(): void {
    if (!this.ctx || !this.portBed || this.tavMusicSrc) return;
    const buf = this.samples.get('music_tavern'); if (!buf) return;
    const s = this.ctx.createBufferSource(); s.buffer = buf; s.loop = true; s.connect(this.portBed.tavMusic); s.start();
    this.tavMusicSrc = s;
  }

  /** Attach the battle theme as a silent loop the moment its track lands (combatPing rides the gain up/down). */
  private attachBattleLoop(): void {
    if (!this.ctx || !this.battleGain || this.battleSrc) return;
    const buf = this.samples.get('music_battle'); if (!buf) return;
    const s = this.ctx.createBufferSource(); s.buffer = buf; s.loop = true; s.connect(this.battleGain); s.start();
    this.battleSrc = s;
  }

  /** Attach the night bed as a silent loop the moment a night track lands (setNight rides the gain in/out). */
  private attachNightLoop(): void {
    if (!this.ctx || !this.nightGain || this.nightSrc) return;
    const buf = NIGHT_TRACKS.map((n) => this.samples.get(n)).find(Boolean); if (!buf) return;
    const s = this.ctx.createBufferSource(); s.buffer = buf; s.loop = true; s.connect(this.nightGain); s.start();
    this.nightSrc = s;
  }

  /** Trickle the rest of the calm rotation in behind the entry track — one fetch every CALM_DRIP_MS, low and
   *  slow so the background variety never competes with the entry burst (or a phone's data plan). Kicked once,
   *  after the bed starts; stops when every calm track is in. */
  private startCalmDrip(): void {
    if (this.calmDripTimer) return;
    const tick = (): void => {
      this.calmDripTimer = null;
      const next = CALM_TRACKS.find((n) => !this.samples.has(n) && !this.musicRequested.has(n));
      if (!next) return; // rotation fully streamed
      this.ensureMusic(next);
      this.calmDripTimer = setTimeout(tick, CALM_DRIP_MS);
    };
    this.calmDripTimer = setTimeout(tick, CALM_DRIP_MS);
  }

  setMuted(m: boolean): void {
    this.muted = m;
    if (this.master && this.ctx) this.master.gain.setTargetAtTime(m ? 0 : 0.6 * this.masterVol, this.ctx.currentTime, 0.05);
  }
  isMuted(): boolean { return this.muted; }

  /* -------- Settings › Audio: per-category volume (0..1 gain). Safe to call before the ctx exists (stored,
   *  applied when the graph is built). Everything is a no-op-clean multiplier, so 1.0 == the shipped mix. -- */
  private static clampGain(v: number): number { return !Number.isFinite(v) || v < 0 ? 0 : v > 4 ? 4 : v; }
  setMasterVolume(v01: number): void {
    this.masterVol = Audio.clampGain(v01);
    if (this.master && this.ctx) this.master.gain.setTargetAtTime(this.muted ? 0 : 0.6 * this.masterVol, this.ctx.currentTime, 0.05);
  }
  setSfxVolume(v01: number): void {
    this.sfxVol = Audio.clampGain(v01);
    if (this.sfxCat && this.ctx) this.sfxCat.gain.setTargetAtTime(this.sfxVol, this.ctx.currentTime, 0.05);
  }
  setAmbianceVolume(v01: number): void {
    this.ambVol = Audio.clampGain(v01);
    if (this.ambCat && this.ctx) this.ambCat.gain.setTargetAtTime(this.ambVol, this.ctx.currentTime, 0.05);
  }
  setMusicVolume(v01: number): void {
    this.musVol = Audio.clampGain(v01);
    if (this.musCat && this.ctx) this.musCat.gain.setTargetAtTime(this.musVol, this.ctx.currentTime, 0.05);
  }
  /** Onda Social (v40): incoming-voice volume (Settings › Audio → Voice slider). */
  setVoiceVolume(v01: number): void {
    this.voiceVol = Audio.clampGain(v01);
    if (this.voiceCat && this.ctx) this.voiceCat.gain.setTargetAtTime(this.voiceVol, this.ctx.currentTime, 0.05);
  }
  /** Onda Social (v40): the shared AudioContext (or null before a gesture) — the voice engine hangs its peer
   *  panners off `voiceBusNode()`. `resume()` builds both on the first gesture (mic-enable counts). */
  voiceContext(): AudioContext | null { return this.ctx; }
  voiceBusNode(): GainNode | null { return this.voiceCat; }
  /** Routing helpers: a source connects to its category bus (or straight to master before the buses exist). */
  private sfxOut(): AudioNode { return this.sfxCat ?? this.master!; }
  private ambOut(): AudioNode { return this.ambCat ?? this.master!; }
  private musOut(): AudioNode { return this.musCat ?? this.master!; }

  private makeNoise(seconds: number): AudioBuffer {
    const ctx = this.ctx!;
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * seconds), ctx.sampleRate);
    const d = buf.getChannelData(0);
    let s = 0x9e3779b9; // deterministic-ish; audio doesn't need crypto randomness
    for (let i = 0; i < d.length; i++) { s = (s * 1664525 + 1013904223) >>> 0; d[i] = (s / 0xffffffff) * 2 - 1; }
    return buf;
  }

  /** Distance attenuation + stereo pan for a world event relative to the local ship. */
  private place(dist: number, pan: number): { gain: number; pan: number } | null {
    if (dist > MAX_DIST) return null;
    return { gain: Math.max(0, 1 - dist / MAX_DIST) ** 1.4, pan: Math.max(-1, Math.min(1, pan)) };
  }

  /** Play a generated sample as a spatial one-shot. Returns false if the sample isn't loaded (→ synth). */
  private playSample(name: string, gainV: number, panV: number, rate = 1): boolean {
    const buf = this.samples.get(name);
    if (!buf || !this.ctx || !this.master) return false;
    if (this.muted) return true;                  // "handled" — don't fall through to synth while muted
    if (this.voices >= VOICE_CAP) return true;
    const src = this.ctx.createBufferSource(); src.buffer = buf; src.playbackRate.value = rate;
    const g = this.ctx.createGain(); g.gain.value = gainV * (SAMPLES[name] ?? 1);
    const p = this.ctx.createStereoPanner(); p.pan.value = panV;
    src.connect(g); g.connect(p); p.connect(this.sfxOut());
    this.voices++;
    setTimeout(() => { this.voices = Math.max(0, this.voices - 1); }, Math.min(4000, buf.duration * 1000 + 80));
    src.start();
    return true;
  }

  private node(panV: number): { input: AudioNode; out: GainNode } | null {
    if (!this.ctx || !this.master || this.muted) return null;
    if (this.voices >= VOICE_CAP) return null;
    const g = this.ctx.createGain();
    const p = this.ctx.createStereoPanner();
    p.pan.value = panV;
    g.connect(p); p.connect(this.sfxOut());
    this.voices++;
    setTimeout(() => { this.voices = Math.max(0, this.voices - 1); }, 1600);
    return { input: g, out: g };
  }

  private burst(buf: AudioBuffer, when: number, dur: number, type: BiquadFilterType, freq: number, freqEnd: number, q: number, dest: AudioNode): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource(); src.buffer = buf;
    const f = ctx.createBiquadFilter(); f.type = type; f.Q.value = q;
    f.frequency.setValueAtTime(freq, when);
    if (freqEnd !== freq) f.frequency.exponentialRampToValueAtTime(Math.max(20, freqEnd), when + dur);
    src.connect(f); f.connect(dest);
    src.start(when); src.stop(when + dur + 0.05);
  }

  private tone(when: number, dur: number, f0: number, f1: number, kind: OscillatorType, peak: number, dest: AudioNode): void {
    const ctx = this.ctx!;
    const o = ctx.createOscillator(); o.type = kind;
    o.frequency.setValueAtTime(f0, when);
    if (f1 !== f0) o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), when + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, when);
    g.gain.exponentialRampToValueAtTime(peak, when + Math.min(0.02, dur * 0.2));
    g.gain.exponentialRampToValueAtTime(0.0001, when + dur);
    o.connect(g); g.connect(dest);
    o.start(when); o.stop(when + dur + 0.02);
  }

  /** Cannon fire — generated boom (pitched a touch by ammo) or a deep synth boom fallback. */
  fire(ammo: number, dist: number, pan: number): void {
    const pl = this.place(dist, pan); if (!pl) return;
    if (this.playSample('cannon_fire', pl.gain, pl.pan, ammo === 2 ? 1.12 : ammo === 1 ? 0.95 : 1)) return;
    const n = this.node(pl.pan); if (!n || !this.ctx || !this.noise) return;
    const now = this.ctx.currentTime, g = n.out;
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(pl.gain * 0.98, now + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
    this.burst(this.noise, now, 0.04, 'highpass', 2200, 1400, 0.7, g);
    this.burst(this.noise, now, 0.3, 'lowpass', ammo === 2 ? 1100 : 520, 130, 0.7, g);
    this.tone(now, 0.24, ammo === 1 ? 72 : 92, 36, 'sine', pl.gain * 0.85, g);
    this.tone(now, 0.3, 50, 26, 'sine', pl.gain * 0.55, g);
  }

  /** Cannonball strikes a hull — generated THUD (with a hair of distance delay) or a synth thud. */
  hit(dist: number, pan: number): void {
    const pl = this.place(dist, pan); if (!pl) return;
    if (this.playSample('cannon_hit', pl.gain, pl.pan)) return;
    const n = this.node(pl.pan); if (!n || !this.ctx || !this.noise) return;
    const when = this.ctx.currentTime + Math.min(0.12, dist / 900), g = n.out;
    g.gain.setValueAtTime(pl.gain * 0.92, when);
    g.gain.exponentialRampToValueAtTime(0.0001, when + 0.15);
    this.burst(this.noise, when, 0.05, 'highpass', 1800, 1200, 0.8, g);
    this.burst(this.noise, when, 0.12, 'bandpass', 240, 170, 1.4, g);
    this.tone(when, 0.11, 150, 55, 'triangle', pl.gain * 0.45, g);
    this.tone(when, 0.13, 110, 36, 'sine', pl.gain * 0.6, g);
  }

  /** A cannonball ends its flight in the sea — a splash. */
  splash(dist: number, pan: number, big = false): void {
    const pl = this.place(dist, pan); if (!pl) return;
    if (this.playSample('splash', pl.gain * (big ? 1 : 0.7), pl.pan, big ? 0.92 : 1.08)) return;
    const n = this.node(pl.pan); if (!n || !this.ctx || !this.noise) return;
    const now = this.ctx.currentTime, g = n.out;
    const amp = pl.gain * (big ? 0.5 : 0.3);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(amp, now + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, now + (big ? 0.4 : 0.3));
    this.burst(this.noise, now, big ? 0.4 : 0.3, 'highpass', 900, 2600, 0.4, g);
  }

  /** Hull-vs-hull ram — heavy wooden crunch scaled by impact magnitude (0..1). */
  ram(mag: number, dist: number, pan: number): void {
    const pl = this.place(dist, pan); if (!pl) return;
    const k = 0.4 + 0.6 * mag;
    if (this.playSample('ram', pl.gain * k, pl.pan, 0.92 + mag * 0.16)) return;
    const n = this.node(pl.pan); if (!n || !this.ctx || !this.noise) return;
    const now = this.ctx.currentTime, g = n.out;
    g.gain.setValueAtTime(pl.gain * 0.9 * k, now);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
    this.burst(this.noise, now, 0.4, 'lowpass', 380, 90, 0.6, g);
    this.tone(now, 0.3, 60, 30, 'sine', pl.gain * 0.7 * k, g);
  }

  /** A ship sinks — a long descending gurgle/groan. */
  sink(dist: number, pan: number): void {
    const pl = this.place(dist, pan); if (!pl) return;
    if (this.playSample('sink', pl.gain, pl.pan)) return;
    const n = this.node(pl.pan); if (!n || !this.ctx || !this.noise) return;
    const now = this.ctx.currentTime, g = n.out;
    g.gain.setValueAtTime(pl.gain * 0.5, now);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 1.1);
    this.burst(this.noise, now, 1.0, 'lowpass', 700, 120, 0.8, g);
    this.tone(now, 0.9, 180, 55, 'sawtooth', pl.gain * 0.25, g);
  }

  /** Grapple tether snaps on — a short rope/chain creak. */
  grapple(dist: number, pan: number): void {
    const pl = this.place(dist, pan); if (!pl) return;
    if (this.playSample('grapple', pl.gain, pl.pan)) return;
    const n = this.node(pl.pan); if (!n || !this.ctx || !this.noise) return;
    const now = this.ctx.currentTime, g = n.out;
    g.gain.setValueAtTime(pl.gain * 0.4, now);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
    this.burst(this.noise, now, 0.22, 'bandpass', 1600, 800, 3, g);
  }

  /** Treasure / raid success — generated coin jingle or a synth arpeggio. */
  coins(): void {
    if (this.playSample('coins', 0.9, 0)) return;
    const n = this.node(0); if (!n || !this.ctx) return;
    const now = this.ctx.currentTime, g = n.out; g.gain.value = 0.6;
    [660, 880, 1320].forEach((f, i) => this.tone(now + i * 0.07, 0.13, f, f, 'triangle', 0.5, g));
    this.tone(now + 0.17, 0.18, 1760, 2640, 'square', 0.18, g);
  }

  /** YOUR kill — a bright victory sting layered over the coin jingle. */
  kill(): void {
    this.coins();
    const n = this.node(0); if (!n || !this.ctx) return;
    const now = this.ctx.currentTime, g = n.out; g.gain.value = 0.5;
    [523, 659, 784, 1047].forEach((f, i) => this.tone(now + i * 0.06, 0.2, f, f, 'triangle', 0.5, g)); // rising major arpeggio
    this.tone(now + 0.24, 0.28, 784, 1568, 'sawtooth', 0.13, g);                                        // a confident sweep up
  }

  /** Anchor drop — rattling chain running out, a big splash, then a low settling thud.
   *  A Ludo.ai 'anchor' sample overrides this synth once generated. */
  anchor(): void {
    if (this.playSample('anchor', 0.8, 0)) return;
    const n = this.node(0); if (!n || !this.ctx || !this.noise) return;
    const now = this.ctx.currentTime, g = n.out; g.gain.value = 0.7;
    for (let i = 0; i < 7; i++) { // chain rattle: descending metallic noise bursts
      const src = this.ctx.createBufferSource(); src.buffer = this.noise;
      const bp = this.ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 2600 - i * 170; bp.Q.value = 6;
      const eg = this.ctx.createGain(); const at = now + i * 0.045;
      eg.gain.setValueAtTime(0.0001, at); eg.gain.exponentialRampToValueAtTime(0.5 - i * 0.03, at + 0.008); eg.gain.exponentialRampToValueAtTime(0.0001, at + 0.05);
      src.connect(bp); bp.connect(eg); eg.connect(g); src.start(at); src.stop(at + 0.07);
    }
    const splashAt = now + 0.34;                       // splash as it hits the water
    const sp = this.ctx.createBufferSource(); sp.buffer = this.noise;
    const lp = this.ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.setValueAtTime(1800, splashAt); lp.frequency.exponentialRampToValueAtTime(300, splashAt + 0.35);
    const sg = this.ctx.createGain(); sg.gain.setValueAtTime(0.55, splashAt); sg.gain.exponentialRampToValueAtTime(0.0001, splashAt + 0.4);
    sp.connect(lp); lp.connect(sg); sg.connect(g); sp.start(splashAt); sp.stop(splashAt + 0.45);
    this.tone(splashAt + 0.02, 0.3, 90, 45, 'sine', 0.5, g); // heavy settling thud
  }

  /** Hit confirmation — a crisp high "tink" when YOUR shot lands (always synth — abstract UI cue). */
  hitConfirm(): void {
    const n = this.node(0); if (!n || !this.ctx) return;
    const now = this.ctx.currentTime, g = n.out; g.gain.value = 0.5;
    this.tone(now, 0.07, 1400, 1400, 'square', 0.38, g);
    this.tone(now + 0.04, 0.1, 2000, 2500, 'square', 0.28, g);
  }

  /** HUD 2.0 low-integrity heartbeat — a soft low "lub-dub" while the hull is nearly gone. Quiet and
   *  round (sine thumps) so it reads as tension, not noise; the CombatFx overlay paces the cadence. */
  heartbeat(): void {
    const n = this.node(0); if (!n || !this.ctx) return;
    const now = this.ctx.currentTime, g = n.out; g.gain.value = 0.5;
    this.tone(now, 0.11, 62, 38, 'sine', 0.5, g);          // lub
    this.tone(now + 0.16, 0.09, 55, 34, 'sine', 0.34, g);  // dub
  }

  /** Near-miss WHISTLE — a shot that screamed past the hull without landing. A quick descending air-rush
   *  "vwip", panned L/R by the ball's camera-relative bearing so you hear which side it flew by. Always
   *  synth (an abstract danger cue); attenuated + panned via the shared spatial node. */
  whistle(dist: number, pan: number): void {
    const pl = this.place(dist, pan); if (!pl) return;
    const n = this.node(pl.pan); if (!n || !this.ctx) return;
    const now = this.ctx.currentTime, g = n.out; g.gain.value = pl.gain * 0.6;
    this.tone(now, 0.26, 2300, 720, 'sine', 0.32, g);       // the falling whistle
    this.tone(now, 0.22, 3400, 1100, 'triangle', 0.12, g);  // a thin overtone for the air-rush edge
  }

  /** Base-under-raid ALARM — an urgent struck-bell clang for the DEFENDER (always synth: a clear UI cue). */
  alarm(): void {
    const n = this.node(0); if (!n || !this.ctx) return;
    const now = this.ctx.currentTime, g = n.out; g.gain.value = 0.6;
    for (const at of [0, 0.3, 0.6]) {            // clang-clang-clang
      this.tone(now + at, 0.5, 880, 840, 'triangle', 0.5, g);
      this.tone(now + at, 0.45, 1318, 1290, 'square', 0.2, g); // metallic overtone
      this.tone(now + at, 0.32, 587, 560, 'sine', 0.32, g);    // low body
    }
  }

  /** Negative outcome (raid repelled / you were raided) — a short descending fail tone. */
  negative(): void {
    const n = this.node(0); if (!n || !this.ctx) return;
    const now = this.ctx.currentTime, g = n.out; g.gain.value = 0.6;
    this.tone(now, 0.18, 320, 150, 'sawtooth', 0.4, g);
    this.tone(now + 0.12, 0.22, 240, 110, 'sawtooth', 0.32, g);
  }

  /** Course plotted on the chart (map-ux): a rubber-stamp THUMP into the paper, a short quill scratch
   *  drawing the voyage line, then a small brass compass ping as the needle settles. Always synth. */
  chartPlot(): void {
    const n = this.node(0); if (!n || !this.ctx || !this.noise) return;
    const now = this.ctx.currentTime, g = n.out; g.gain.value = 0.6;
    // stamp thump — a felt-padded thud with a papery noise transient
    this.tone(now, 0.11, 150, 62, 'sine', 0.55, g);
    this.burst(this.noise, now, 0.06, 'lowpass', 900, 300, 0.9, g);
    // quill scratch — thin band-passed noise dragged across the sheet
    const sc = this.ctx.createBufferSource(); sc.buffer = this.noise;
    const bp = this.ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.Q.value = 2.2;
    bp.frequency.setValueAtTime(2100, now + 0.1); bp.frequency.exponentialRampToValueAtTime(3300, now + 0.3);
    const eg = this.ctx.createGain();
    eg.gain.setValueAtTime(0.0001, now + 0.1); eg.gain.exponentialRampToValueAtTime(0.16, now + 0.14);
    eg.gain.exponentialRampToValueAtTime(0.0001, now + 0.32);
    sc.connect(bp); bp.connect(eg); eg.connect(g); sc.start(now + 0.1); sc.stop(now + 0.36);
    // compass ping — the needle finds the bearing
    this.tone(now + 0.3, 0.22, 1560, 1490, 'triangle', 0.16, g);
  }

  /** Soft UI tick for button taps. */
  ui(): void {
    const n = this.node(0); if (!n || !this.ctx) return;
    const now = this.ctx.currentTime, g = n.out;
    g.gain.setValueAtTime(0.18, now);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
    this.tone(now, 0.05, 900, 600, 'square', 0.12, g);
  }

  /* ---- A Draga (gacha ritual) — a winch ratchet + rarity-scaled reveal stings ---- */

  /** One winch ratchet click; `t01` (0..1) raises the pitch as the chest nears the surface (the reel's
   *  "heartbeat accelerating"). Always synth — an abstract mechanical cue. */
  dragaTick(t01 = 0): void {
    const n = this.node(0); if (!n || !this.ctx || !this.noise) return;
    const now = this.ctx.currentTime, g = n.out; g.gain.value = 0.45;
    const src = this.ctx.createBufferSource(); src.buffer = this.noise;
    const bp = this.ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 1600 + t01 * 2400; bp.Q.value = 9;
    const eg = this.ctx.createGain(); eg.gain.setValueAtTime(0.0001, now); eg.gain.exponentialRampToValueAtTime(0.5, now + 0.006); eg.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
    src.connect(bp); bp.connect(eg); eg.connect(g); src.start(now); src.stop(now + 0.07);
    this.tone(now, 0.04, 260 + t01 * 520, 180, 'square', 0.12, g);
  }

  /** A deep splash + surge as the chest breaches the surface. */
  dragaBreach(): void {
    const n = this.node(0); if (!n || !this.ctx || !this.noise) return;
    const now = this.ctx.currentTime, g = n.out; g.gain.value = 0.7;
    const sp = this.ctx.createBufferSource(); sp.buffer = this.noise;
    const lp = this.ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.setValueAtTime(2200, now); lp.frequency.exponentialRampToValueAtTime(300, now + 0.5);
    const sg = this.ctx.createGain(); sg.gain.setValueAtTime(0.6, now); sg.gain.exponentialRampToValueAtTime(0.0001, now + 0.55);
    sp.connect(lp); lp.connect(sg); sg.connect(g); sp.start(now); sp.stop(now + 0.6);
    this.tone(now, 0.35, 120, 60, 'sine', 0.5, g);
  }

  /** The reveal sting, ESCALATED by rarity (0 common … 3 legendary). */
  dragaReveal(rarity: number): void {
    const n = this.node(0); if (!n || !this.ctx) return;
    const now = this.ctx.currentTime, g = n.out; g.gain.value = 0.55;
    if (rarity <= 0) { this.tone(now, 0.08, 520, 300, 'triangle', 0.4, g); this.tone(now + 0.05, 0.1, 900, 720, 'sine', 0.2, g); return; }
    if (rarity === 1) { [523, 784, 1047].forEach((f, i) => this.tone(now + i * 0.05, 0.16, f, f, 'triangle', 0.45, g)); this.tone(now + 0.16, 0.2, 1047, 1568, 'sine', 0.2, g); return; }
    if (rarity === 2) { [196, 262, 330].forEach((f) => this.tone(now, 0.7, f, f, 'sawtooth', 0.16, g)); [659, 784, 988].forEach((f, i) => this.tone(now + 0.2 + i * 0.07, 0.24, f, f, 'triangle', 0.4, g)); this.tone(now + 0.5, 0.5, 494, 988, 'sine', 0.2, g); return; }
    this.dragaLegendary();
  }

  /** The legendary fanfare — a brass rise into a triumphant major chord + gold shimmer + a coin tail. */
  dragaLegendary(): void {
    const n = this.node(0); if (!n || !this.ctx) return;
    const now = this.ctx.currentTime, g = n.out; g.gain.value = 0.62;
    this.tone(now, 0.5, 262, 523, 'sawtooth', 0.22, g);
    [523, 659, 784, 1047].forEach((f) => this.tone(now + 0.5, 0.9, f, f, 'triangle', 0.4, g));
    [1568, 2093].forEach((f, i) => this.tone(now + 0.6 + i * 0.12, 0.7, f, f, 'sine', 0.2, g));
    this.tone(now + 0.5, 1.0, 131, 131, 'sine', 0.34, g);
    setTimeout(() => this.coins(), 700);
  }

  /** Combat just happened (you fired or were hit) — crossfade the music to the battle theme for a
   *  while, then drift back to the calm sailing bed. No-op until the music tracks are loaded. */
  combatPing(): void {
    if (!this.ctx || (!this.battleGain && !this.calmGain)) return;
    this.ensureMusic('music_battle'); // COLD-LOAD DIET: stream the battle theme on first combat (attaches when it lands)
    const now = this.ctx.currentTime;
    this.inCombat = true;
    this.battleGain?.gain.setTargetAtTime(0.6, now, 0.7);
    this.calmGain?.gain.setTargetAtTime(0.0, now, 0.7);   // duck both beds (they keep sequencing underneath)
    this.nightGain?.gain.setTargetAtTime(0.0, now, 0.7);
    if (this.combatTimer) clearTimeout(this.combatTimer);
    this.combatTimer = setTimeout(() => {
      if (!this.ctx) return;
      this.inCombat = false;
      const t2 = this.ctx.currentTime;
      this.battleGain?.gain.setTargetAtTime(0.0, t2, 2);   // restore whichever bed matches the time of day
      this.nightGain?.gain.setTargetAtTime(this.nightMix * 0.55, t2, 2);
      this.calmGain?.gain.setTargetAtTime((1 - this.nightMix) * 0.5, t2, 2);
    }, 14000);
  }

  /** Crossfade the music bed between the day (calm) and night (nocturne) themes by the day/night
   *  darkness factor (0..1). Called each frame; throttled, and yields to the battle theme in combat. */
  setNight(f: number): void {
    // COLD-LOAD DIET: begin streaming the night bed as dusk approaches (well before it's audible at f>0.5), so
    // the track has landed by the time the crossfade wants it.
    if (f > 0.35 && this.ctx) this.ensureMusic(NIGHT_TRACKS[0]!);
    const target = f > 0.5 ? Math.min(1, (f - 0.5) / 0.25) : 0; // ramp in over the darkest quarter
    if (Math.abs(target - this.nightMix) < 0.02) return;
    this.nightMix = target;
    if (!this.ctx || this.inCombat) return;                     // battle owns the mix while fighting
    const now = this.ctx.currentTime;
    this.nightGain?.gain.setTargetAtTime(target * 0.55, now, 2.5);
    this.calmGain?.gain.setTargetAtTime((1 - target) * 0.5, now, 2.5);
  }

  /**
   * The storm bed — FOUR SYNTHESIZED LAYERS, not one loop.
   *
   * NOT ONE DOWNLOADED BYTE. Everything below is built out of the same 1 s white-noise buffer the whole
   * engine already reuses, shaped by biquads and an LFO. The boot budget is untouched (see the cold-load
   * diet note at the top of this file) and, more to the point, a single looping ambiance clip is exactly
   * what makes weather sound canned: you hear the seam, and the storm sounds identical at first gust and
   * at full fury. Synthesized layers can each track the intensity on their own curve, which is what makes
   * a storm sound like it is BUILDING rather than fading up.
   *
   *   WIND  — lowpass noise. As the gale rises the cutoff opens (240 → ~1.5 kHz), so the wind does not
   *           just get louder, it gets BRIGHTER and starts to whistle. This is the layer that carries the
   *           sense of force.
   *   GUST  — an LFO on the wind gain. A flat wind is a fan; a breathing one is weather. Slow, ±35%.
   *   RAIN  — highpass noise, played back fast (a denser hiss). Comes in later than the wind, exactly as
   *           the visuals do (STORM.RAIN_START), so you HEAR the squall arrive before you're wet.
   *   WASH  — a bandpass roar around 300 Hz: the sea itself breaking, not the rain. Only at real fury.
   *           This is the difference between "it is raining" and "the sea is up".
   *
   * The generated `storm_rain` ambiance, when it happens to be loaded, rides UNDER all of it at reduced
   * gain as a bed — it costs nothing extra (it is already in the eager sample bank) and it fills the
   * midrange the synth layers deliberately leave open.
   */
  private ensureStormBed(): void {
    if (this.stormGain || !this.ctx || !this.master || !this.noise) return;
    const ctx = this.ctx;
    const bus = ctx.createGain(); bus.gain.value = 0; bus.connect(this.ambOut()); this.stormGain = bus;

    const loopNoise = (rate: number): AudioBufferSourceNode => {
      const s = ctx.createBufferSource(); s.buffer = this.noise; s.loop = true; s.playbackRate.value = rate; s.start();
      return s;
    };
    // --- wind: lowpass noise whose cutoff AND level ride the fury ---
    const windFilter = ctx.createBiquadFilter(); windFilter.type = 'lowpass'; windFilter.frequency.value = 240; windFilter.Q.value = 1.4;
    const wind = ctx.createGain(); wind.gain.value = 0;
    loopNoise(0.6).connect(windFilter); windFilter.connect(wind); wind.connect(bus);
    // --- gust: a slow LFO breathing on top of the wind gain (an offset gain node it modulates) ---
    const gust = ctx.createGain(); gust.gain.value = 0;
    const gustLfo = ctx.createOscillator(); gustLfo.type = 'sine'; gustLfo.frequency.value = 0.13;
    gustLfo.connect(gust.gain); gustLfo.start();
    windFilter.connect(gust); gust.connect(bus);
    // --- rain: bright hiss, arrives after the wind ---
    const rainFilter = ctx.createBiquadFilter(); rainFilter.type = 'highpass'; rainFilter.frequency.value = 3200;
    const rain = ctx.createGain(); rain.gain.value = 0;
    loopNoise(1.9).connect(rainFilter); rainFilter.connect(rain); rain.connect(bus);
    // --- wash: the sea's own roar, low and wide, only at real fury ---
    const washFilter = ctx.createBiquadFilter(); washFilter.type = 'bandpass'; washFilter.frequency.value = 300; washFilter.Q.value = 0.6;
    const wash = ctx.createGain(); wash.gain.value = 0;
    loopNoise(0.35).connect(washFilter); washFilter.connect(wash); wash.connect(bus);
    // --- the generated ambiance rides underneath, if it happens to be loaded (no extra fetch) ---
    const sample = this.samples.get('storm_rain');
    if (sample) {
      const s = ctx.createBufferSource(); s.buffer = sample; s.loop = true;
      const g = ctx.createGain(); g.gain.value = 0.45;
      s.connect(g); g.connect(bus); s.start();
    }
    this.stormRig = { wind, windFilter, gust, gustLfo, rain, wash, washFilter };
  }

  /**
   * Drive the whole storm mix from one intensity (0..1). Called every frame; every write is a
   * `setTargetAtTime` ramp, so the layers CROSSFADE rather than step, and a captain sailing out of a
   * squall hears it recede instead of switch off.
   *
   * Built lazily on the first frame a storm actually exists, so a calm session never allocates an
   * oscillator, and a session that never sees weather never pays for any of it.
   */
  setStorm(f: number): void {
    if (!this.ctx) return;
    if (f > 0.002) this.ensureStormBed();
    if (!this.stormGain) return;
    const s = Math.max(0, Math.min(1, f));
    this.stormLevel = s;
    const now = this.ctx.currentTime;
    const target = this.muted ? 0 : s * 0.5;
    if (Math.abs(target - this.stormBedLevel) >= 0.02) {
      this.stormBedLevel = target;
      this.stormGain.gain.setTargetAtTime(target, now, 1.2);
    }
    const rig = this.stormRig;
    if (!rig) return;
    // Each layer has its OWN curve against intensity — that is the whole point of synthesizing them.
    // Wind is there from the first breath; rain joins at the same threshold the visuals use; the sea's
    // wash only shows up once it is genuinely rough.
    const rain01 = Math.max(0, (s - 0.22) / 0.55);
    const wash01 = Math.max(0, (s - 0.45) / 0.55);
    rig.wind.gain.setTargetAtTime(0.30 + 0.55 * s, now, 1.4);
    // the gale doesn't only get louder — it opens up and starts to whistle
    rig.windFilter.frequency.setTargetAtTime(240 + 1300 * s * s, now, 1.6);
    rig.gust.gain.setTargetAtTime(0.35 * s, now, 1.4);          // LFO depth (it modulates around 0)
    rig.gustLfo.frequency.setTargetAtTime(0.10 + 0.14 * s, now, 2.0);
    rig.rain.gain.setTargetAtTime(0.30 * Math.min(1, rain01), now, 1.4);
    rig.wash.gain.setTargetAtTime(0.26 * Math.min(1, wash01), now, 1.8);
    rig.washFilter.frequency.setTargetAtTime(240 + 180 * s, now, 2.0);
    this.maybeCreak(now, s);
  }

  /* ------------------------------ Onda Velas: the canvas ------------------------------ */

  private canvasAt = 0;      // ctx time the next canvas crack is due
  private luffLevel = 0;     // smoothed luff (so a heading wobble can't machine-gun the cracks)

  /**
   * THE SOUND OF A SAIL THAT ISN'T WORKING. Every layer synthesized — zero bytes downloaded, in the same
   * spirit as the storm bed (`ensureStormBed`) it shares the wind with.
   *
   * A drawing sail is silent: the wind is IN it, the cloth is a rigid wing and all you hear is the bow wave.
   * The moment it comes aback it starts to FLOG — a hard, dry crack as several hundred square feet of canvas
   * turns itself inside out, then a lower thump as the boltrope takes the load. That crack is the single most
   * useful piece of audio feedback in a sailing game, because it is the boat telling you you are pointing the
   * wrong way BEFORE the speedo does.
   *
   * Rate and brightness both ride the luff, and every interval carries a wide random spread so the cracks
   * never find a beat (the same discipline as `maybeCreak` — the instant it becomes rhythm it reads as a loop).
   * A gale cracks harder and more often. Call it every frame; it schedules nothing until the canvas is
   * genuinely aback, so a captain sailing well never allocates a node.
   */
  setSailStress(luff: number, storm = 0): void {
    if (!this.ctx || !this.noise || this.muted) return;
    const l = Math.max(0, Math.min(1, luff));
    this.luffLevel += (l - this.luffLevel) * 0.12;
    const fury = Math.max(0, Math.min(1, storm));
    // At real fury even a DRAWING sail finds a crack — the leech snapping as the gusts come through. It only
    // opens above ~0.8 storm, so a normal squall never turns into a metronome.
    const s = Math.max(this.luffLevel, fury * 0.38);
    if (s < 0.3) return;                                   // drawing (or barely lifting) — nothing to hear
    const now = this.ctx.currentTime;
    if (now < this.canvasAt) return;
    // ~2/s at a full flog in a gale, down to one every couple of seconds when it is merely lifting
    this.canvasAt = now + (0.24 + Math.random() * 0.85) / (0.35 + s * (0.9 + fury * 0.8));
    const ctx = this.ctx;
    const g = ctx.createGain(); g.connect(this.ambOut());
    const vol = (0.055 + 0.085 * s) * (0.55 + 0.75 * Math.random()) * (1 + 0.35 * fury);
    const dur = 0.1 + Math.random() * 0.11;
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(vol, now + 0.006);  // canvas cracks: essentially no attack at all
    g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    // the CRACK: a bright band of noise that dives as the cloth loses its shape
    const src = ctx.createBufferSource(); src.buffer = this.noise;
    src.playbackRate.value = 0.85 + Math.random() * 0.6;
    const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.Q.value = 1.5 + Math.random() * 1.6;
    const f0 = 900 + Math.random() * 1500 + fury * 500;
    bp.frequency.setValueAtTime(f0, now);
    bp.frequency.exponentialRampToValueAtTime(Math.max(220, f0 * 0.38), now + dur);
    src.connect(bp); bp.connect(g); src.start(now); src.stop(now + dur + 0.05);
    // …and the BOLTROPE taking the shock a heartbeat later — the low thump that gives the crack its weight
    const th = ctx.createBufferSource(); th.buffer = this.noise; th.playbackRate.value = 0.22;
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 190; lp.Q.value = 3;
    const tg = ctx.createGain(); tg.gain.setValueAtTime(0.0001, now + 0.012);
    tg.gain.exponentialRampToValueAtTime(0.55 + 0.4 * s, now + 0.03);
    tg.gain.exponentialRampToValueAtTime(0.0001, now + dur + 0.16);
    th.connect(lp); lp.connect(tg); tg.connect(g); th.start(now + 0.012); th.stop(now + dur + 0.2);
    setTimeout(() => { try { g.disconnect(); } catch { /* already gone */ } }, (dur + 0.5) * 1000);
  }

  /**
   * The ship complaining. Sparse, low, wooden groans scheduled while the sea is up — a swept low
   * bandpass on noise plus a detuned sine that bends, which is what a loaded timber actually sounds
   * like. Rare on purpose (one every few seconds at full fury, never in a light blow): it is the detail
   * that makes the deck feel like it is under strain, and it stops working the moment it becomes rhythm.
   */
  private maybeCreak(now: number, storm: number): void {
    if (this.muted || storm < 0.45 || !this.ctx || !this.noise) return;
    if (now < this.creakAt) return;
    // fiercer sea, more frequent complaints — with a wide random spread so it never finds a beat
    this.creakAt = now + (1.6 + Math.random() * 5.0) / Math.max(0.2, storm);
    const ctx = this.ctx;
    const g = ctx.createGain(); g.connect(this.ambOut());
    const vol = (0.05 + 0.07 * storm) * (0.6 + Math.random() * 0.7);
    const dur = 0.5 + Math.random() * 0.8;
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(vol, now + 0.12);
    g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    // the timber: a narrow band of noise swept downward (the fibres letting go)
    const src = ctx.createBufferSource(); src.buffer = this.noise; src.playbackRate.value = 0.3 + Math.random() * 0.3;
    const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.Q.value = 7 + Math.random() * 6;
    const f0 = 130 + Math.random() * 180;
    bp.frequency.setValueAtTime(f0, now);
    bp.frequency.exponentialRampToValueAtTime(Math.max(60, f0 * 0.55), now + dur);
    src.connect(bp); bp.connect(g); src.start(now); src.stop(now + dur + 0.05);
    // …and the hull resonating with it: a sine that bends a couple of semitones as the load shifts
    const osc = ctx.createOscillator(); osc.type = 'sine';
    osc.frequency.setValueAtTime(f0 * 0.5, now);
    osc.frequency.exponentialRampToValueAtTime(f0 * 0.5 * (0.82 + Math.random() * 0.12), now + dur);
    const og = ctx.createGain(); og.gain.value = 0.5;
    osc.connect(og); og.connect(g); osc.start(now); osc.stop(now + dur + 0.05);
    setTimeout(() => { try { g.disconnect(); } catch { /* already gone */ } }, (dur + 0.4) * 1000);
  }

  /** A thunderclap (loosely after a lightning flash): the generated sample when loaded, else a synth
   *  crack + long low rumble. Scaled by storm intensity. */
  thunder(f = 1): void {
    if (!this.ctx || this.muted) return;
    // pick a random LOADED variation so strikes don't repeat the same clip
    const loaded = THUNDERS.filter((n) => this.samples.has(n));
    if (loaded.length) {
      const pick = loaded[(Math.random() * loaded.length) | 0]!;
      if (this.playSample(pick, 0.55 + 0.45 * f, (Math.random() * 2 - 1) * 0.5)) return;
    }
    if (!this.noise) return;
    const ctx = this.ctx, now = ctx.currentTime;
    const g = ctx.createGain(); g.connect(this.sfxOut());
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.25 + 0.45 * f, now + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 1.7);
    this.burst(this.noise, now, 0.09, 'highpass', 2600, 700, 0.6, g);   // crack
    this.burst(this.noise, now + 0.02, 1.5, 'lowpass', 150, 45, 0.5, g); // rumble
    this.burst(this.noise, now + 0.18, 1.2, 'lowpass', 95, 38, 0.4, g);  // deep tail
    setTimeout(() => { try { g.disconnect(); } catch { /* already gone */ } }, 2000);
  }

  /* ------------------------------ wave 2b activity SFX ------------------------------ */

  /** Fishing one-shots (always at your own ship → non-spatial). Each falls back to a synth cue. */
  fishingCast(): void { if (!this.playSample('fishing_cast', 0.7, 0)) this.splash(18, 0, false); }
  fishingBite(): void { if (!this.playSample('fishing_bite', 0.9, 0)) this.hitConfirm(); }
  fishingCatch(): void { if (!this.playSample('fishing_catch', 0.85, 0)) this.coins(); }
  /** Wreck-diving one-shots (2b.3). */
  diveSplash(): void { if (!this.playSample('dive_splash', 0.85, 0)) this.splash(15, 0, true); }
  surfaceGasp(): void { if (!this.playSample('surface_gasp', 0.85, 0)) this.splash(15, 0, false); }
  /** Crafting/menu one-shots (2b.5/2b.8). */
  brewPotion(): void { if (!this.playSample('brew_potion', 0.8, 0)) this.ui(); }
  forgeHammer(): void { if (!this.playSample('forge_hammer', 0.85, 0)) this.ui(); }
  marketTrade(): void { if (!this.playSample('market_trade', 0.8, 0)) this.coins(); }
  pageTurn(): void { if (!this.playSample('page_turn', 0.6, 0)) this.ui(); }
  /** Tavern-casino one-shots (Art+Audio wave) — each falls back to a synth cue if its sample is missing. */
  cardDeal(): void { if (!this.playSample('card_deal', 0.7, 0)) this.ui(); }
  chipBet(): void { if (!this.playSample('chip_bet', 0.75, 0)) this.ui(); }
  slotSpin(): void { if (!this.playSample('slot_spin', 0.8, 0)) this.ui(); }
  slotWin(): void { if (!this.playSample('slot_win', 0.9, 0)) this.coins(); }
  /* ---- Slot-machine reel spin (casino polish) — ALL synthesized, no eager audio asset (cold-load safe). ---- */
  /** Start the sustained reel "whir": a band-passed noise rush that runs while the reels turn. Idempotent;
   *  paired with slotWhirStop(). Built on the shared 1 s noise buffer, so it costs nothing at boot. */
  slotWhirStart(): void {
    this.resume();
    if (!this.ctx || !this.noise || this.slotWhir) return;
    const ctx = this.ctx;
    const src = ctx.createBufferSource(); src.buffer = this.noise; src.loop = true;
    const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 300; bp.Q.value = 1.3;
    const hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 130;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.05, ctx.currentTime + 0.09);
    src.connect(bp); bp.connect(hp); hp.connect(g); g.connect(this.sfxOut());
    try { src.start(); } catch { /* already started */ }
    this.slotWhir = { src, gain: g };
  }
  /** Fade + stop the reel whir. Safe when nothing is running. */
  slotWhirStop(): void {
    const w = this.slotWhir; this.slotWhir = null;
    if (!w || !this.ctx) return;
    const now = this.ctx.currentTime;
    try {
      w.gain.gain.cancelScheduledValues(now);
      w.gain.gain.setValueAtTime(Math.max(0.0001, w.gain.gain.value as number), now);
      w.gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.07);
      w.src.stop(now + 0.1);
    } catch { /* already stopped */ }
  }
  /** A reel landing "thunk" — a filtered noise click + a short low tone. `idx` (0..2) nudges the pitch up
   *  left→right so the three reels stopping read as a rising mechanical cadence. Always synth. */
  slotReelStop(idx = 0): void {
    const n = this.node(0); if (!n || !this.ctx || !this.noise) return;
    const now = this.ctx.currentTime, g = n.out;
    this.burst(this.noise, now, 0.05, 'lowpass', 1500, 300, 0.6, g);
    this.tone(now, 0.1, 200 + idx * 45, 84, 'square', 0.24, g);
  }

  /* ---- Onda Dealers — the croupier voice clips (lazy: only fetched once the captain reaches a casino) ---- */
  private dealerVoicesRequested = false;
  /** Fetch + decode the pre-generated dealer voice clips (`public/audio/dealer/<id>.mp3`) into the sample bank,
   *  once, on the first casino entry. Missing/failed clips simply leave that line silent (balloon still shows).
   *  Keyed `dealer/<id>` so they never collide with the eager SAMPLES bank. No-op before a gesture built the ctx. */
  ensureDealerVoices(clipIds: readonly string[]): void {
    if (this.dealerVoicesRequested || !this.ctx) return;
    this.dealerVoicesRequested = true;
    for (const id of clipIds) {
      void (async () => {
        try {
          const r = await fetch(`${AUDIO_BASE}dealer/${id}.mp3`);
          if (!r.ok) return;
          this.samples.set(`dealer/${id}`, await this.ctx!.decodeAudioData(await r.arrayBuffer()));
        } catch { /* leave this line voiceless — the balloon carries it */ }
      })();
    }
  }
  /** Play a dealer line as a moderate, positional one-shot on the sfx bus (mute + sfx-volume respected via
   *  playSample). `pan` is the table's screen-x (−1..1); returns false if the clip hasn't loaded (→ balloon only). */
  dealerVoice(clipId: string, pan = 0): boolean {
    return this.playSample(`dealer/${clipId}`, 0.85, Math.max(-1, Math.min(1, pan)));
  }
  /** Onda 8 Interior Vivo — a soft wooden floorboard footstep (local avatar inside the house, non-spatial).
   *  Always synth: a short muffled thud (filtered noise body + a low knock), pitch varied per step so a walk
   *  never machine-guns the same click. Cadence + pitch are driven from the avatar's real speed in main.ts. */
  footstep(pitch = 1): void {
    const n = this.node(0); if (!n || !this.ctx || !this.noise) return;
    const now = this.ctx.currentTime, g = n.out; g.gain.value = 0.42;
    this.burst(this.noise, now, 0.05, 'lowpass', 330 * pitch, 120, 0.8, g);   // muffled wooden body
    this.tone(now, 0.045, 155 * pitch, 68, 'sine', 0.24, g);                   // low creak/knock
  }

  /** Wave 3 event one-shots. */
  whaleCall(): void { if (!this.playSample('whale_call', 0.95, 0)) this.thunder(); }
  bountyWanted(): void { if (!this.playSample('bounty_wanted', 0.9, 0)) this.alarm(); }
  cursedSting(): void { if (!this.playSample('cursed_sting', 0.9, 0)) this.negative(); }

  private gullsGain: GainNode | null = null;
  private gullsLevel = -1;
  private ensureGullsBed(): void {
    if (this.gullsGain || !this.ctx || !this.master) return;
    const sample = this.samples.get('gulls_loop');
    if (!sample) return;
    const bus = this.ctx.createGain(); bus.gain.value = 0; bus.connect(this.ambOut()); this.gullsGain = bus;
    const s = this.ctx.createBufferSource(); s.buffer = sample; s.loop = true; s.connect(bus); s.start();
  }
  /** Looping gulls bed near fishing shoals — volume tracks how close the nearest shoal is (0..1). */
  setGulls(f: number): void {
    if (!this.ctx) return;
    if (f > 0.01) this.ensureGullsBed();
    if (!this.gullsGain) return;
    const target = this.muted ? 0 : Math.min(1, f) * 0.4;
    if (Math.abs(target - this.gullsLevel) < 0.02) return;
    this.gullsLevel = target;
    this.gullsGain.gain.setTargetAtTime(target, this.ctx.currentTime, 0.8);
  }

  /** Mark a gesture happened; start the ambient bed + music once their samples are ready. */
  /**
   * Wave 6: the walkable-port audio. While ashore the sea beds duck out and a plaza ambiance bed plays,
   * crossfading toward the tavern ambiance + music as `tavernMix` (0..1, from proximity to the inn) rises.
   * Sea beds restore on exit. Degrades silently if the port samples never loaded.
   */
  setPortAmbient(inPort: boolean, tavernMix: number, isCapital = false): void {
    const ctx = this.ctx, master = this.master;
    if (!ctx || !master) return;
    // COLD-LOAD DIET: the tavern shanty (a full music track) streams only once ashore; a late-attach wires it into
    // the port bed if the bed was already built before it landed (so a first port visit still gets tavern music).
    if (inPort) this.ensureMusic('music_tavern');
    // Capital-only crowd murmur: a discreet layer under the port ambiance (built lazily once its sample is in).
    // Eases down a touch inside the tavern so the shanty/chatter there isn't muddied.
    if (inPort && isCapital && !this.cityGain && this.samples.has('city_ambiance')) {
      const g = ctx.createGain(); g.gain.value = 0; g.connect(this.ambOut());
      const s = ctx.createBufferSource(); s.buffer = this.samples.get('city_ambiance')!; s.loop = true; s.connect(g); s.start();
      this.cityGain = g;
    }
    if (this.cityGain) {
      const cm = this.muted ? 0 : 1;
      this.cityGain.gain.setTargetAtTime(inPort && isCapital ? 0.26 * (1 - 0.5 * tavernMix) * cm : 0, ctx.currentTime, 0.6);
    }
    // build the port beds once, on first entry, if the samples are in
    if (inPort && !this.portBed && (this.samples.has('port_ambiance') || this.samples.has('tavern_ambiance'))) {
      const bed = (name: string, vol: number): GainNode => {
        const g = ctx.createGain(); g.gain.value = 0; g.connect(this.ambOut());
        const buf = this.samples.get(name);
        if (buf) { const s = ctx.createBufferSource(); s.buffer = buf; s.loop = true; s.connect(g); s.start(); if (name === 'music_tavern') this.tavMusicSrc = s; }
        void vol; return g;
      };
      this.portBed = { port: bed('port_ambiance', 0.5), tavAmb: bed('tavern_ambiance', 0.5), tavMusic: bed('music_tavern', 0.45) };
    }
    if (inPort !== this.ashore) {
      this.ashore = inPort;
      const seaVol = inPort ? 0 : 1;
      // duck / restore the sea beds (ambient + music + storm) so the port isn't muddied by the ocean bed
      if (this.ambientGain) this.ambientGain.gain.setTargetAtTime((this.muted ? 0 : 0.55) * seaVol, ctx.currentTime, 0.6);
      if (this.musicBus) this.musicBus.gain.setTargetAtTime((this.muted ? 0 : 0.42) * seaVol, ctx.currentTime, 0.6);
      if (this.stormGain && inPort) this.stormGain.gain.setTargetAtTime(0, ctx.currentTime, 0.6);
    }
    const b = this.portBed; if (!b) return;
    const m = this.muted ? 0 : 1;
    b.port.gain.setTargetAtTime(inPort ? 0.5 * (1 - tavernMix) * m : 0, ctx.currentTime, 0.5);
    b.tavAmb.gain.setTargetAtTime(inPort ? 0.5 * tavernMix * m : 0, ctx.currentTime, 0.5);
    b.tavMusic.gain.setTargetAtTime(inPort ? 0.45 * tavernMix * m : 0, ctx.currentTime, 0.5);
  }

  /* ------------------------------ Kill-Cam / Highlight replay audio ------------------------------ */
  private replaySrc: AudioBufferSourceNode | null = null;
  private replayGain: GainNode | null = null;
  private replayDuck: { amb: number; mus: number; storm: number } | null = null;
  private capDest: MediaStreamAudioDestinationNode | null = null;
  private replayBuffers = new Map<string, AudioBuffer>();

  /** Kill-cam: fetch a highlight track lazily (cold-load safe — never on the boot path). `/audio/replay/<name>.mp3`.
   *  Missing/failed → null (the replay simply runs silent). */
  private async ensureReplayTrack(name: string): Promise<AudioBuffer | null> {
    if (!this.ctx) return null;
    const cached = this.replayBuffers.get(name); if (cached) return cached;
    try {
      const r = await fetch(`${AUDIO_BASE}replay/${name}.mp3`);
      if (!r.ok) return null;
      const buf = await this.ctx.decodeAudioData(await r.arrayBuffer());
      this.replayBuffers.set(name, buf);
      return buf;
    } catch { return null; }
  }

  /** Kill-cam: DUCK the live sea beds, stream the highlight track through the music bus, and (when `wantCapture`)
   *  tee it into a MediaStreamAudioDestination so MediaRecorder can bake the music into the exported clip. Returns
   *  the capture MediaStream (audio) + the track length; a silent result if audio is unavailable or the track is
   *  absent. Never throws. */
  async beginReplay(trackName: string, wantCapture: boolean): Promise<{ stream: MediaStream | null; durationMs: number }> {
    this.resume();
    if (!this.ctx) return { stream: null, durationMs: 0 };
    const ctx = this.ctx, now = ctx.currentTime;
    this.replayDuck = { amb: this.ambientGain?.gain.value ?? 0.55, mus: this.musicBus?.gain.value ?? 0.42, storm: this.stormGain?.gain.value ?? 0 };
    this.ambientGain?.gain.setTargetAtTime(0, now, 0.25);
    this.musicBus?.gain.setTargetAtTime(0, now, 0.25);
    this.stormGain?.gain.setTargetAtTime(0, now, 0.25);
    if (this.portBed) { this.portBed.port.gain.setTargetAtTime(0, now, 0.25); this.portBed.tavAmb.gain.setTargetAtTime(0, now, 0.25); this.portBed.tavMusic.gain.setTargetAtTime(0, now, 0.25); }
    const buf = await this.ensureReplayTrack(trackName);
    const g = ctx.createGain(); g.gain.value = this.muted ? 0 : 0.9; g.connect(this.musOut());
    this.replayGain = g;
    let stream: MediaStream | null = null;
    if (wantCapture) {
      try { const dest = ctx.createMediaStreamDestination(); g.connect(dest); this.capDest = dest; stream = dest.stream; } catch { stream = null; }
    }
    let durationMs = 0;
    if (buf) { const s = ctx.createBufferSource(); s.buffer = buf; s.loop = true; s.connect(g); s.start(); this.replaySrc = s; durationMs = buf.duration * 1000; }
    return { stream, durationMs };
  }

  /** Kill-cam: stop the highlight track + restore the ducked sea beds to their pre-replay levels. */
  endReplay(): void {
    if (!this.ctx) { this.replaySrc = null; this.replayGain = null; this.capDest = null; this.replayDuck = null; return; }
    const now = this.ctx.currentTime;
    try { this.replaySrc?.stop(); } catch { /* already stopped */ }
    this.replaySrc = null;
    if (this.replayGain) { const gg = this.replayGain; try { gg.gain.setTargetAtTime(0, now, 0.2); } catch { /* */ } setTimeout(() => { try { gg.disconnect(); } catch { /* */ } }, 500); this.replayGain = null; }
    if (this.capDest) { try { this.capDest.disconnect(); } catch { /* */ } this.capDest = null; }
    if (this.replayDuck) {
      const d = this.replayDuck;
      this.ambientGain?.gain.setTargetAtTime(this.muted ? 0 : d.amb, now, 0.6);
      this.musicBus?.gain.setTargetAtTime(this.muted ? 0 : d.mus, now, 0.6);
      this.replayDuck = null;
    }
  }

  startAmbient(): void {
    this.armed = true;
    // COLD-LOAD DIET: stream just ONE calm track now (the entry bed); the rest of the rotation trickles in via
    // the calm drip once the bus is up, and battle/night/tavern stream only when their context nears.
    this.ensureMusic(ENTRY_MUSIC);
    this.ensureAudioBeds();
  }

  private ensureAudioBeds(): void {
    if (!this.armed || !this.ctx || !this.master) return;
    if (!this.ambientStarted && this.samples.has('ambient_sea')) {
      const bus = this.ctx.createGain(); bus.gain.value = 0; bus.connect(this.ambOut()); this.ambientGain = bus;
      bus.gain.setTargetAtTime(this.muted ? 0 : 0.55, this.ctx.currentTime, 2);
      const s = this.ctx.createBufferSource(); s.buffer = this.samples.get('ambient_sea')!; s.loop = true;
      s.connect(bus); s.start();
      this.ambientStarted = true;
    }
    if (!this.musicStarted && (CALM_TRACKS.some((n) => this.samples.has(n)) || this.samples.has('music_battle'))) {
      this.startMusic();
      this.musicStarted = true;
    }
  }

  /** Music on a shared bus: a rotating playlist of relaxing tracks (the bed) + a looping "battle"
   *  theme that sits silent until combatPing() crossfades it up. */
  private startMusic(): void {
    const ctx = this.ctx, master = this.master;
    if (!ctx || !master || this.musicBus) return;
    const bus = ctx.createGain(); bus.gain.value = this.muted ? 0 : 0.42; bus.connect(this.musOut()); this.musicBus = bus;
    // battle theme: a silent loop that crossfades up during combat. COLD-LOAD DIET: the track streams lazily
    // (ensureMusic on the first combatPing), so the gain is built now and its source is attached when it lands.
    this.battleGain = ctx.createGain(); this.battleGain.gain.value = 0; this.battleGain.connect(bus);
    this.attachBattleLoop(); // no-op until the battle track has streamed in
    // night bed: a nocturnal loop, silent until setNight() crossfades it in as the cycle darkens (also lazy).
    this.nightGain = ctx.createGain(); this.nightGain.gain.value = 0; this.nightGain.connect(bus);
    this.attachNightLoop(); // no-op until a night track has streamed in
    // calm bed: rotate through every relaxing track present (computed live, so late-loaded tracks join in).
    this.calmGain = ctx.createGain(); this.calmGain.gain.value = 0.5; this.calmGain.connect(bus);
    if (CALM_TRACKS.some((n) => this.samples.has(n))) {
      this.calmIdx = (performance.now() | 0) % CALM_TRACKS.length; // varied start (no fixed first track)
      this.playNextCalm();
    } else if (this.samples.has('music_battle')) {
      this.battleGain.gain.value = 0.5; // only the battle track exists -> use it as the bed
    }
    this.startCalmDrip(); // trickle the remaining calm tracks in behind the entry bed
  }

  /** Play the next relaxing track once through, then advance — a never-repeating background bed.
   *  The list is recomputed each time so tracks that finish downloading later join the rotation.
   *  Keeps sequencing even while ducked during combat, so it's always "somewhere" when it returns. */
  private playNextCalm(): void {
    const ctx = this.ctx;
    if (!ctx || !this.calmGain) return;
    const list = CALM_TRACKS.filter((n) => this.samples.has(n));
    if (!list.length) return;
    const buf = this.samples.get(list[this.calmIdx % list.length]!)!;
    const s = ctx.createBufferSource(); s.buffer = buf; s.connect(this.calmGain);
    s.onended = () => { if (this.calmSrc === s) { this.calmIdx++; this.playNextCalm(); } };
    this.calmSrc = s; s.start();
  }

  private startSynthAmbient(): void {
    if (this.ambientStarted || !this.ctx || !this.master || !this.noise) return;
    this.ambientStarted = true;
    const ctx = this.ctx;
    const bus = ctx.createGain(); bus.gain.value = 0.0; bus.connect(this.ambOut());
    this.ambientGain = bus;
    bus.gain.setTargetAtTime(this.muted ? 0 : 0.22, ctx.currentTime, 2);
    const sea = ctx.createBufferSource(); sea.buffer = this.noise; sea.loop = true;
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 360; lp.Q.value = 0.5;
    const lfo = ctx.createOscillator(); lfo.frequency.value = 0.08; const lfoG = ctx.createGain(); lfoG.gain.value = 160;
    lfo.connect(lfoG); lfoG.connect(lp.frequency);
    const seaG = ctx.createGain(); seaG.gain.value = 0.6;
    sea.connect(lp); lp.connect(seaG); seaG.connect(bus);
    const wind = ctx.createBufferSource(); wind.buffer = this.noise; wind.loop = true;
    const hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 700;
    const windG = ctx.createGain(); windG.gain.value = 0.12;
    const wlfo = ctx.createOscillator(); wlfo.frequency.value = 0.05; const wlfoG = ctx.createGain(); wlfoG.gain.value = 0.08;
    wlfo.connect(wlfoG); wlfoG.connect(windG.gain);
    wind.connect(hp); hp.connect(windG); windG.connect(bus);
    sea.start(); wind.start(); lfo.start(); wlfo.start();
  }
}
